import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";

function PwField({ label, value, onChange, testid, autoComplete }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-mono">{label}</Label>
      <Input type="password" required minLength={autoComplete === "current-password" ? 1 : 8} autoComplete={autoComplete}
        value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

export function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrent(""); setNext(""); setConfirm(""); setError("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) { setError("New passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed. Your other devices have been signed out.");
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Change password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <PwField label="Current password" value={current} onChange={setCurrent} autoComplete="current-password" testid="change-password-current-input" />
          <PwField label="New password (min 8 characters)" value={next} onChange={setNext} autoComplete="new-password" testid="change-password-new-input" />
          <PwField label="Confirm new password" value={confirm} onChange={setConfirm} autoComplete="new-password" testid="change-password-confirm-input" />
          {error && <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="change-password-error">{error}</div>}
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="change-password-submit-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
